import React, {useState, useEffect, useMemo} from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Collapse,
  useTheme,
  Tooltip,
  Stack,
  Autocomplete
} from '@mui/material'
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material'
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import { config } from '../../config/environment'

interface AnalyticsEvent {
  eventId: string
  userId: string
  userEmail: string
  userDepartment: string
  sessionId: string
  eventType: string
  action: string
  timestamp: number
  toolName?: string
  pageName?: string
  stepName?: string
  stepNumber?: number
  properties?: string
  duration?: number
}

interface AnalyticsEventsData {
  events: AnalyticsEvent[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  dateRange: {
    startDate: string
    endDate: string
  }
  filterOptions: {
    eventTypes: string[]
    actions: string[]
    tools: string[]
    pages: string[]
    userEmails: string[]
    departments: string[]
    sessionIds: string[]
  }
}

interface Filters {
  eventType: string
  action: string
  tool: string
  page: string
  userEmail: string
  department: string
  sessionId: string
  searchText: string
}

interface AnalyticsEventsViewerProps {
  onError: (error: string | null) => void
  refreshTrigger: boolean
}

/**
 * Analytics Events Viewer Component
 * Provides comprehensive filtering and viewing for analytics events
 * Implements hybrid backend/frontend filtering approach
 */
const AnalyticsEventsViewer: React.FC<AnalyticsEventsViewerProps> = ({
  onError,
  refreshTrigger
}) => {
  const theme = useTheme()

  // Data state
  const [data, setData] = useState<AnalyticsEventsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)

  // Date range state (backend filtering)
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, 'day')) // Last week
  const [endDate, setEndDate] = useState<Dayjs>(dayjs())

  // Frontend filters state
  const [filters, setFilters] = useState<Filters>({
    eventType: '',
    action: '',
    tool: '',
    page: '',
    userEmail: '',
    department: '',
    sessionId: '',
    searchText: ''
  })

  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null)

  // Get API base URL
  const getApiBaseUrl = () => {
    return config.API_BASE_URL
  }

  // Fetch analytics events from backend
  const fetchAnalyticsEvents = async () => {
    try {
      setIsLoading(true)
      onError(null)

      const token = localStorage.getItem('dashboard_access_token')
      if (!token) {
        onError('Authentication token not found')
        return
      }

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        page: (page + 1).toString(),
        pageSize: rowsPerPage.toString(),
        orderBy: 'timestamp',
        orderDirection: 'DESC'
      })

      const response = await fetch(
        `${getApiBaseUrl()}/api/db-admin/analytics-events?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch analytics events')
      }

      setData(result.data)
    } catch (error) {
      console.error('Error fetching analytics events:', error)
      onError(
        error instanceof Error
          ? error.message
          : 'Failed to fetch analytics events'
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Apply frontend filters to events
  const filteredEvents = useMemo(() => {
    if (!data?.events) return []

    return data.events.filter((event) => {
      // Event type filter
      if (filters.eventType && event.eventType !== filters.eventType)
        return false

      // Action filter
      if (filters.action && event.action !== filters.action) return false

      // Tool filter
      if (filters.tool && event.toolName !== filters.tool) return false

      // Page filter
      if (filters.page && event.pageName !== filters.page) return false

      // User email filter
      if (filters.userEmail && event.userEmail !== filters.userEmail)
        return false

      // Department filter
      if (filters.department && event.userDepartment !== filters.department)
        return false

      // Session ID filter
      if (filters.sessionId && event.sessionId !== filters.sessionId)
        return false

      // Search text filter (searches across multiple fields)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase()
        const searchableText = [
          event.eventType,
          event.action,
          event.toolName,
          event.pageName,
          event.stepName,
          event.userEmail,
          event.userDepartment,
          event.sessionId,
          event.properties
        ]
          .join(' ')
          .toLowerCase()

        if (!searchableText.includes(searchLower)) return false
      }

      return true
    })
  }, [data?.events, filters])

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchAnalyticsEvents()
  }, [startDate, endDate, page, rowsPerPage, refreshTrigger])

  // Handle filter changes
  const handleFilterChange = (filterKey: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value
    }))
  }

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      eventType: '',
      action: '',
      tool: '',
      page: '',
      userEmail: '',
      department: '',
      sessionId: '',
      searchText: ''
    })
  }

  // Handle page change
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Format timestamp (Unix timestamp in milliseconds)
  const formatTimestamp = (timestamp: number) => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')
  }

  // Format properties JSON
  const formatProperties = (properties?: string) => {
    if (!properties) return 'None'
    try {
      const parsed = JSON.parse(properties)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return properties
    }
  }

  // Export filtered data
  const handleExport = () => {
    const csvContent = [
      // Header
      'Event ID,User Email,Department,Event Type,Action,Tool,Page,Step,Timestamp,Duration,Properties',
      // Data rows
      ...filteredEvents.map((event) =>
        [
          event.eventId,
          event.userEmail || '',
          event.userDepartment || '',
          event.eventType,
          event.action,
          event.toolName || '',
          event.pageName || '',
          event.stepName || '',
          formatTimestamp(event.timestamp),
          event.duration || '',
          (event.properties || '').replace(/"/g, '""') // Escape quotes
        ].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], {type: 'text/csv'})
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-events-${
      new Date().toISOString().split('T')[0]
    }.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const activeFiltersCount = Object.values(filters).filter(Boolean).length

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3
            }}
          >
            <Box>
              <Typography variant="h6" component="h3">
                Analytics Events
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data
                  ? `${filteredEvents.length} of ${data.total} events`
                  : 'Loading...'}
                {data &&
                  ` • ${data.dateRange.startDate.split('T')[0]} to ${
                    data.dateRange.endDate.split('T')[0]
                  }`}
              </Typography>
            </Box>
            <Box sx={{display: 'flex', gap: 1}}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterIcon />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              >
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={filteredEvents.length === 0}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchAnalyticsEvents}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filters Section */}
          <Collapse in={filtersExpanded}>
            <Paper variant="outlined" sx={{p: 2, mb: 3}}>
              <Grid container spacing={2}>
                {/* Date Range Filters */}
                <Grid item xs={12} md={3}>
                  <DateTimePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(value) => value && setStartDate(dayjs(value))}
                    slotProps={{textField: {size: 'small', fullWidth: true}}}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DateTimePicker
                    label="End Date"
                    value={endDate}
                    onChange={(value) => value && setEndDate(dayjs(value))}
                    slotProps={{textField: {size: 'small', fullWidth: true}}}
                  />
                </Grid>

                {/* Search Text */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Search"
                    placeholder="Search across all fields..."
                    value={filters.searchText}
                    onChange={(e) =>
                      handleFilterChange('searchText', e.target.value)
                    }
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{mr: 1, color: 'text.secondary'}} />
                      )
                    }}
                  />
                </Grid>

                {/* Dropdown Filters */}
                {data?.filterOptions && (
                  <>
                    <Grid item xs={12} md={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Event Type</InputLabel>
                        <Select
                          value={filters.eventType}
                          label="Event Type"
                          onChange={(e) =>
                            handleFilterChange('eventType', e.target.value)
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          {data.filterOptions.eventTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Action</InputLabel>
                        <Select
                          value={filters.action}
                          label="Action"
                          onChange={(e) =>
                            handleFilterChange('action', e.target.value)
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          {data.filterOptions.actions.map((action) => (
                            <MenuItem key={action} value={action}>
                              {action}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Tool</InputLabel>
                        <Select
                          value={filters.tool}
                          label="Tool"
                          onChange={(e) =>
                            handleFilterChange('tool', e.target.value)
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          {data.filterOptions.tools.map((tool) => (
                            <MenuItem key={tool} value={tool}>
                              {tool}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Page</InputLabel>
                        <Select
                          value={filters.page}
                          label="Page"
                          onChange={(e) =>
                            handleFilterChange('page', e.target.value)
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          {data.filterOptions.pages?.map((page) => (
                            <MenuItem key={page} value={page}>
                              {page}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Autocomplete
                        options={data.filterOptions.userEmails}
                        value={filters.userEmail}
                        onChange={(_, value) =>
                          handleFilterChange('userEmail', value || '')
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="User Email"
                            size="small"
                            fullWidth
                          />
                        )}
                        freeSolo
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={filters.department}
                          label="Department"
                          onChange={(e) =>
                            handleFilterChange('department', e.target.value)
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          {data.filterOptions.departments.map((dept) => (
                            <MenuItem key={dept} value={dept}>
                              {dept}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={clearAllFilters}
                        disabled={activeFiltersCount === 0}
                        startIcon={<ClearIcon />}
                        fullWidth
                      >
                        Clear
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </Collapse>

          {/* Events Table */}
          {isLoading ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress />
            </Box>
          ) : filteredEvents.length > 0 ? (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}></TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Event Type</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Tool</TableCell>
                      <TableCell>Page</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEvents.map((event) => (
                      <React.Fragment key={event.eventId}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedEventId(
                                  expandedEventId === event.eventId
                                    ? null
                                    : event.eventId
                                )
                              }
                            >
                              {expandedEventId === event.eventId ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTimestamp(event.timestamp)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {event.userEmail}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {event.userDepartment}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.eventType}
                              size="small"
                              variant="outlined"
                              color={
                                event.eventType === 'error'
                                  ? 'error'
                                  : event.eventType === 'tool_loaded'
                                  ? 'success'
                                  : event.eventType === 'step_progression'
                                  ? 'info'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.action}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.toolName || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.pageName || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {event.stepName || '-'}
                              </Typography>
                              {event.stepNumber && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Step {event.stepNumber}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {event.duration ? `${event.duration}ms` : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{paddingBottom: 0, paddingTop: 0}}
                            colSpan={9}
                          >
                            <Collapse in={expandedEventId === event.eventId}>
                              <Box sx={{p: 2, bgcolor: 'grey.50'}}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Event Details
                                    </Typography>
                                    <Stack spacing={1}>
                                      <Typography variant="body2">
                                        <strong>Event ID:</strong>{' '}
                                        {event.eventId}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>User ID:</strong>{' '}
                                        {event.userId}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Session ID:</strong>{' '}
                                        {event.sessionId}
                                      </Typography>
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Properties
                                    </Typography>
                                    <Paper
                                      variant="outlined"
                                      sx={{p: 1, bgcolor: 'background.paper'}}
                                    >
                                      <Typography
                                        variant="caption"
                                        component="pre"
                                        sx={{
                                          whiteSpace: 'pre-wrap',
                                          fontFamily: 'monospace'
                                        }}
                                      >
                                        {formatProperties(event.properties)}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={data?.total || 0}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[50, 100, 200]}
                showFirstButton
                showLastButton
              />
            </>
          ) : (
            <Paper sx={{p: 4, textAlign: 'center'}}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Analytics Events Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your date range or clearing filters to see more
                events
              </Typography>
            </Paper>
          )}
        </CardContent>
      </Card>
    </LocalizationProvider>
  )
}

export default AnalyticsEventsViewer
